import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { useMediaQuery } from "@mui/material";

import Button from "@mui/material/Button";
import { Container } from "@mui/material";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import { Fab } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkIcon from "@mui/icons-material/Link";

import { questionsList } from "../quiz/content";
import { calculateScores } from "./helper";
import AlertDialog from "../../util/confirmDialog";
import ResultColours from "./services/ResultColours";
import { animals } from "./services/ResultAnimals";
import AnimalRadarChart from "./charts/RadarChart";

import { QUIZ_LENGTH } from "../..";
import { CustomGrid, classes } from "./style";

export const sortResults = (data) =>
  [...data].sort(({ score: scoreA }, { score: scoreB }) =>
    scoreA > scoreB ? -1 : 1
  );

export const ResultsScene = () => {
  const navigate = useNavigate();

  // If the link was shared, answers string will be in URL
  const urlParams = new URLSearchParams(window.location.search);
  const urlAnswers = urlParams.get("answers");

  const answerParam = urlAnswers || sessionStorage.getItem("answers") || "";

  const { colours_main } = ResultColours();

  const [loaded, setLoaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sortedResults, setSortedResults] = useState([]);
  const [topResults, setTopResults] = useState([]);
  const [topOccupations, setTopOccupations] = useState([]);
  const [selectedResult, setSelectedResult] = useState({});
  const [linkCopied, setLinkCopied] = useState(false);

  // Handle if answerParam is empty but user is on the results page
  useEffect(() => {
    if (!answerParam || answerParam === "0".repeat(QUIZ_LENGTH)) {
      navigate({
        pathname: "/quiz",
      });
    }
  }, [answerParam, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      setLoaded(false);

      const results = calculateScores(answerParam, questionsList);
      setSortedResults(results);

      const topScore = results[0].score;
      const filteredTopResults = results.filter(
        (result) => result.score === topScore
      );
      setTopResults(filteredTopResults);
      setSelectedResult(filteredTopResults[0]);

      try {
        const response = await axios.get(
          "https://services.onetcenter.org/ws/mnm/interestprofiler/careers",
          {
            headers: { Accept: "application/json" },
            params: { answers: answerParam, client: "cybera_ca" },
          }
        );

        const occupations = response.data.career.slice(0, 15);
        setTopOccupations(occupations);
      } catch (error) {
        console.error("Error fetching occupations:", error);
      }

      setLoaded(true);
    };

    fetchData();
  }, [answerParam]);

  const navigateToAnimals = () => {
    navigate({
      pathname: "/animals",
    });
  };

  const onClickOpen = () => setConfirmOpen(true);
  const onClose = () => setConfirmOpen(false);

  const onAnimalSelect = (animal) => {
    setSelectedResult(animal);
  };

  const getShareableLink = () => {
    const currentPath = window.location.pathname;
    // Place answers string in URL to share results
    const shareableUrl = `${
      window.location.origin
    }${currentPath}?answers=${encodeURIComponent(answerParam)}`;
    setLinkCopied(true);
    return shareableUrl;
  };

  const onRestart = async () => {
    navigate({
      pathname: "/quiz",
    });

    sessionStorage.setItem("answers", "0".repeat(QUIZ_LENGTH));
    window.location.reload();
  };

  const animalPicture = () => {
    return (
      <div className={classes.previewAnimal}>
        <img
          src={`${process.env.PUBLIC_URL}/assets/animals/${selectedResult.area}.png`}
          loading="lazy"
          alt={animals[selectedResult.area].name}
          className={classes.image}
        ></img>
      </div>
    );
  };

  const isSmallScreen = useMediaQuery(`(max-width:750px)`);

  return !loaded ? (
    <CustomGrid
      container
      direction="column"
      alignItems="center"
      className={classes.root}
    >
      <Skeleton
        variant="rounded"
        width="100%"
        height={500}
        sx={{ marginBottom: "20px" }}
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height={300}
        sx={{ marginBottom: "20px" }}
      />
      <Skeleton
        variant="rounded"
        width="100%"
        height={300}
        sx={{ marginBottom: "20px" }}
      />
    </CustomGrid>
  ) : (
    <CustomGrid
      container
      direction="column"
      alignItems="center"
      justifyContent="flex-start"
      className={classes.root}
    >
      <Grid container>
        <Grid
          size={{ xs: 12 }}
          sx={{
            minWidth: "100%",
            background: colours_main[selectedResult.area].colour,
          }}
        >
          {topResults?.length > 1 && (
            <Container className={classes.resultButtons}>
              {topResults.map((result) => (
                <Button
                  key={result.area}
                  variant={
                    selectedResult.area === result.area
                      ? "contained"
                      : "outlined"
                  }
                  color="white"
                  sx={{
                    margin: "10px 10px 0px 0px",
                    color:
                      selectedResult.area === result.area
                        ? colours_main[selectedResult.area].colour
                        : "white",
                  }}
                  onClick={() => onAnimalSelect(result)}
                >
                  {animals[result.area].name}
                </Button>
              ))}
            </Container>
          )}
          <Container className={classes.previewContainer}>
            <div className={classes.previewText}>
              <Typography variant="body1" sx={{ marginBottom: "10px" }}>
                Your animal personality type is...
              </Typography>
              <div>
                <Typography variant="h3" className={classes.boldText}>
                  {animals[selectedResult.area].name}
                </Typography>
                <Typography variant="h5" className={classes.boldText}>
                  {animals[selectedResult.area].secondaryName}
                </Typography>
              </div>
              {isSmallScreen && animalPicture()}
              <Typography variant="body1" sx={{ margin: "30px 0px" }}>
                {animals[selectedResult.area].description}
              </Typography>
              <Typography variant="h6">Strengths</Typography>
              <div className={classes.strengths}>
                {animals[selectedResult.area].strengths.map((strength) => (
                  <Typography
                    key={strength}
                    variant="body1"
                    sx={{ marginRight: "30px" }}
                  >
                    {strength}
                  </Typography>
                ))}
              </div>
            </div>
            {!isSmallScreen && animalPicture()}
          </Container>
        </Grid>
      </Grid>
      <Container className={classes.padding}>
        <Grid container>
          <Grid size={{ xs: 12 }} sx={{ minWidth: "100%" }}>
            <div className={classes.resultContainer}>
              <Typography variant="h4" className={classes.boldText}>
                Your Results
              </Typography>
              <Button
                variant="contained"
                color={colours_main[selectedResult.area].paletteName}
                sx={{ marginLeft: "10px", color: "white !important" }}
                onClick={() =>
                  navigator.clipboard.writeText(getShareableLink())
                }
              >
                {linkCopied ? "Link copied" : "Share link"}
                <LinkIcon fontSize="small" sx={{ marginLeft: "5px" }} />
              </Button>
            </div>
            <Grid
              container
              className={`${classes.animalScoreContainer} ${classes.container}`}
            >
              {sortedResults.map((result) => (
                <Grid
                  size={{ xs: 6, md: 4, lg: 1 }}
                  key={result.area}
                  className={classes.animalScore}
                >
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/animals/${result.area}.png`}
                    loading="lazy"
                    alt={animals[result.area].name}
                    className={classes.smallImage}
                  ></img>
                  <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                    {animals[result.area].name}
                  </Typography>
                  <Typography variant="h6">{result.score}</Typography>
                </Grid>
              ))}
              <Grid
                size={{ xs: 12, lg: 2 }}
                className={classes.buttonContainer}
              >
                <Button
                  variant="contained"
                  color={colours_main[selectedResult.area].paletteName}
                  sx={{ height: "min-content", color: "white !important" }}
                  onClick={navigateToAnimals}
                >
                  Meet the animals
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container>
          <Grid
            size={{ xs: 12, md: 6 }}
            className={`${classes.container} ${classes.graphContainer}`}
          >
            <AnimalRadarChart
              results={sortedResults}
              graphColour={colours_main[selectedResult.area].colour}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <div
              className={`${classes.container} ${classes.occupationContainer}`}
            >
              <Grid size={{ xs: 12 }}>
                <Typography variant="h6">Your Top Occupations</Typography>
                <div className={classes.occupations}>
                  {topOccupations.map((item, index) => {
                    const linkValue = item.href
                      .split("/careers/")[1]
                      ?.replace("/", "");

                    return (
                      <Typography variant="body1" key={index}>
                        <a
                          href={`https://www.onetonline.org/link/summary/${linkValue}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            color: colours_main[selectedResult.area].colour,
                          }}
                          className={classes.link}
                        >
                          {index + 1}. {item.title}
                        </a>
                      </Typography>
                    );
                  })}
                </div>
              </Grid>
            </div>
          </Grid>
        </Grid>
      </Container>
      <Fab
        variant="extended"
        size="medium"
        color="primary"
        className={classes.startOverButton}
        onClick={onClickOpen}
      >
        <RefreshIcon sx={{ mr: 1 }} />
        Start over
      </Fab>
      <AlertDialog
        title="Restart the quiz?"
        body="Are you sure you want to clear your results and restart the quiz?"
        yes="Restart"
        no="Cancel"
        color="primary"
        open={confirmOpen}
        onClose={onClose}
        onConfirm={onRestart}
      />
    </CustomGrid>
  );
};

export default ResultsScene;
